"use client"

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react"
import { useAuth } from "@/contexts/auth-context"
import { useAtsSources, type AtsSourceInfo } from "@/hooks/use-ats-sources"

/**
 * Session-local ATS source CONTEXT for the source-scoped surfaces (Field Mapping
 * Configuration, the Explorer/Data Pull/Value Mapping tools, and Target Mapping).
 *
 * This is deliberately separate from the tenant's ACTIVE source (managed in the ATS
 * Sources tool and used by NEW sync operations): switching the context here only
 * changes which source you are viewing/editing — it does not re-point the tenant.
 * The default selection is the active source when one is set, otherwise the first
 * configured source. Tenants with no sources yet are surfaced via hasNoSources so
 * callers can render a graceful empty state instead of erroring.
 */
type AtsSourceContextType = {
  sources: AtsSourceInfo[]
  /** The source the user is currently working with (null while loading or when none exist). */
  selectedSource: AtsSourceInfo | null
  /** The selected source's parent etl.CodeAtsSystem code — what the explorer/tools route on. */
  selectedAtsSystem: string | null
  /** Select a source by its etl.CodeAtsSource code. Unknown codes are ignored. */
  setSelectedSourceCode: (code: string) => void
  isLoading: boolean
  error: string | null
  /** True once loading has completed and the tenant has no ATS sources configured. */
  hasNoSources: boolean
  refresh: () => Promise<void>
}

const AtsSourceContext = createContext<AtsSourceContextType | undefined>(undefined)

export function AtsSourceProvider({ children }: { children: ReactNode }) {
  const { token, isAuthenticated } = useAuth()
  const { loadSources } = useAtsSources()
  const [sources, setSources] = useState<AtsSourceInfo[]>([])
  const [selectedCode, setSelectedCode] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const loaded = await loadSources()
      setSources(loaded)
      // Default to the active source, else the first configured source. Preserve an
      // existing valid selection across refreshes so a switch survives a reload.
      setSelectedCode((current) => {
        if (current && loaded.some((s) => s.code.toLowerCase() === current.toLowerCase())) {
          return current
        }
        const fallback = loaded.find((s) => s.isActive) ?? loaded[0]
        return fallback?.code ?? null
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load ATS sources")
      setSources([])
      setSelectedCode(null)
    } finally {
      setIsLoading(false)
    }
    // loadSources is recreated each render; intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isAuthenticated && token) {
      load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token])

  // The switcher only offers valid codes; an unknown code simply resolves to no
  // selection below rather than needing a guard here.
  const setSelectedSourceCode = useCallback((code: string) => setSelectedCode(code), [])

  const selectedSource =
    sources.find((s) => s.code.toLowerCase() === (selectedCode ?? "").toLowerCase()) ?? null
  const selectedAtsSystem = selectedSource?.atsSystem ?? null
  const hasNoSources = !isLoading && !error && sources.length === 0

  return (
    <AtsSourceContext.Provider
      value={{
        sources,
        selectedSource,
        selectedAtsSystem,
        setSelectedSourceCode,
        isLoading,
        error,
        hasNoSources,
        refresh: load,
      }}
    >
      {children}
    </AtsSourceContext.Provider>
  )
}

export function useAtsSource(): AtsSourceContextType {
  const context = useContext(AtsSourceContext)
  if (context === undefined) {
    throw new Error("useAtsSource must be used within an AtsSourceProvider")
  }
  return context
}
